import React, { useEffect, useState } from 'react';
import { TextField, MenuItem, CircularProgress } from '@mui/material';
import { trainerApi } from '../services/api';
import { authService } from '../services/auth';

interface Agent {
  synth_agent_id: number;
  synth_display_name: string;
  note?: string;
  outbound_call_enabled: boolean;
}

// Global cache for agents
let agentsCache: Agent[] | null = null;
let fetchingPromise: Promise<Agent[]> | null = null;

const fetchAgents = async (): Promise<Agent[]> => {
  // Return empty array if not authenticated
  if (!authService.isAuthenticated()) {
    return [];
  }

  if (agentsCache) return agentsCache;
  if (fetchingPromise) return fetchingPromise;

  fetchingPromise = trainerApi.getAgents().then((results: any) => {
    // Backend now filters for outbound agents only, so no need to filter client-side
    const mappedAgents: Agent[] = results
      .map((item: any) => ({
        synth_agent_id: item.synth_agent?.synth_agent_id || 0,
        synth_display_name: item.synth_display_name || item.synth_agent?.synth_name || 'Agent',
        note: item.note || item.synth_agent?.note || '',
        outbound_call_enabled: true // Backend already filters for outbound agents
      }))
      .filter((agent: Agent) => agent.synth_agent_id > 0);
      
    agentsCache = mappedAgents;
    fetchingPromise = null;
    return mappedAgents;
  }).catch((err) => {
    console.error('Error fetching agents:', err);
    fetchingPromise = null;
    throw err;
  });

  return fetchingPromise;
};

// Only prefetch if authenticated
if (authService.isAuthenticated()) {
  fetchAgents().catch(console.error);
}

interface AgentSelectProps {
  value: number | null;
  onChange: (agentId: number | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  sx?: any;
  disabled?: boolean;
}

const AgentSelect: React.FC<AgentSelectProps> = ({ value, onChange, label = 'Agent', error, helperText, sx, disabled }) => {
  const [agents, setAgents] = useState<Agent[]>(agentsCache || []);
  const [loading, setLoading] = useState<boolean>(!agentsCache && authService.isAuthenticated());

  useEffect(() => {
    let isMounted = true;

    const loadAgents = async () => {
      // Don't load if not authenticated
      if (!authService.isAuthenticated()) {
        setLoading(false);
        return;
      }

      if (agentsCache) return;
      
      try {
        const results = await fetchAgents();
        if (isMounted) {
          setAgents(results);
        }
      } catch (error) {
        console.error('Error loading agents:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAgents();
    return () => { isMounted = false; };
  }, []);

  return (
    <TextField
      select
      label={label}
      value={value !== null ? value : ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value as string) : null)}
      fullWidth
      size="small"
      error={error}
      helperText={helperText}
      sx={sx}
      disabled={disabled}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {loading ? (
        <MenuItem value="" disabled>
          <CircularProgress size={20} />
        </MenuItem>
      ) : (
        agents.map((agent) => (
          <MenuItem key={agent.synth_agent_id} value={agent.synth_agent_id}>
            <span>
              {agent.synth_display_name} {agent.note ? <span style={{ color: 'grey', marginLeft: 8 }}>({agent.note})</span> : null}
            </span>
          </MenuItem>
        ))
      )}
    </TextField>
  );
};

export default AgentSelect; 